import { useState, useRef } from 'react';
import { apiClient } from '../contexts/AuthContext';
import { Image, Upload, Link, X, Loader2, Video } from 'lucide-react';
import { toast } from 'sonner';

export function MediaUpload({ onMediaAdded, label = "Photo / Lien", compact = false }) {
  const [mode, setMode] = useState(null); // null | 'upload' | 'link'
  const [linkUrl, setLinkUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10MB)');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await apiClient.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onMediaAdded(data.url);
      toast.success('Photo uploadée !');
      setMode(null);
    } catch (err) {
      toast.error('Erreur upload');
    } finally {
      setUploading(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Vidéo trop volumineuse (max 100MB)');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await apiClient.post('/programs/video', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onMediaAdded(`/api/videos/${data.path}`);
      toast.success('Vidéo uploadée !');
      setMode(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload vidéo');
    } finally {
      setUploading(false);
    }
  };

  const handleLinkAdd = () => {
    if (!linkUrl.trim()) return;
    onMediaAdded(linkUrl.trim());
    setLinkUrl('');
    setMode(null);
  };

  if (mode === null) {
    return (
      <div className={`flex gap-2 ${compact ? '' : 'mt-2'}`}>
        <button
          type="button"
          onClick={() => setMode('upload')}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0a0a0f] border border-[#2A2A2E] rounded-sm text-[11px] text-zinc-400 hover:text-[#FF6B35] hover:border-zinc-600 transition-colors"
          data-testid="media-upload-btn"
        >
          <Upload className="w-3 h-3" /> Photo
        </button>
        <button
          type="button"
          onClick={() => setMode('video')}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0a0a0f] border border-[#2A2A2E] rounded-sm text-[11px] text-zinc-400 hover:text-red-400 hover:border-zinc-600 transition-colors"
          data-testid="media-video-btn"
        >
          <Video className="w-3 h-3" /> Vidéo
        </button>
        <button
          type="button"
          onClick={() => setMode('link')}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0a0a0f] border border-[#2A2A2E] rounded-sm text-[11px] text-zinc-400 hover:text-blue-400 hover:border-zinc-600 transition-colors"
          data-testid="media-link-btn"
        >
          <Link className="w-3 h-3" /> Lien
        </button>
      </div>
    );
  }

  if (mode === 'video') {
    return (
      <div className="flex items-center gap-2 mt-2">
        <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm,video/mpeg" onChange={handleVideoUpload} className="hidden" />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 border border-red-500/30 rounded-sm text-[11px] text-red-400 hover:bg-red-500/20 transition-colors"
          data-testid="media-choose-video"
        >
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Video className="w-3 h-3" />}
          {uploading ? 'Upload en cours...' : 'Choisir vidéo (max 100MB)'}
        </button>
        <button type="button" onClick={() => setMode(null)} className="text-zinc-600 hover:text-zinc-400">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (mode === 'upload') {
    return (
      <div className="flex items-center gap-2 mt-2">
        <input ref={fileRef} type="file" accept="image/*,video/*" onChange={handleFileUpload} className="hidden" />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#FF6B35]/10 border border-[#FF6B35]/30 rounded-sm text-[11px] text-[#FF6B35] hover:bg-[#FF6B35]/20 transition-colors"
          data-testid="media-choose-file"
        >
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
          {uploading ? 'Upload...' : 'Choisir fichier'}
        </button>
        <button type="button" onClick={() => setMode(null)} className="text-zinc-600 hover:text-zinc-400">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 mt-2">
      <input
        value={linkUrl}
        onChange={e => setLinkUrl(e.target.value)}
        placeholder="https://..."
        className="flex-1 bg-[#0a0a0f] border border-[#2A2A2E] text-white rounded-sm px-2 py-1.5 text-xs placeholder-zinc-600"
        data-testid="media-link-input"
      />
      <button type="button" onClick={handleLinkAdd} className="px-2 py-1.5 bg-blue-500/20 text-blue-400 rounded-sm text-xs hover:bg-blue-500/30" data-testid="media-link-add">
        OK
      </button>
      <button type="button" onClick={() => { setMode(null); setLinkUrl(''); }} className="text-zinc-600 hover:text-zinc-400">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export function MediaPreview({ urls = [], onRemove }) {
  if (!urls || urls.length === 0) return null;
  return (
    <div className="flex gap-2 flex-wrap mt-2">
      {urls.map((url, i) => {
        const isVideo = url.includes('/api/videos/') || /\.(mp4|webm|mov)(\?|$)/i.test(url);
        const isImage = !isVideo && (url.match(/\.(jpg|jpeg|png|gif|webp)/i) || url.includes('/api/files/'));
        return (
          <div key={`${url}-${i}`} className="relative group">
            {isVideo ? (
              <div className="w-16 h-16 rounded-sm bg-black border border-red-500/40 flex items-center justify-center">
                <Video className="w-5 h-5 text-red-400" />
              </div>
            ) : isImage ? (
              <img src={url} alt="" className="w-16 h-16 rounded-sm object-cover border border-[#2A2A2E]" />
            ) : (
              <div className="w-16 h-16 rounded-sm bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                <Link className="w-4 h-4 text-blue-400" />
              </div>
            )}
            {onRemove && (
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
